import { serialize as serializeCookie } from 'cookie';

const AUTH_SESSION_HINT_COOKIE_BASE_NAME = 'imgenhancer.auth_hint';
const SECURE_COOKIE_PREFIX = '__Secure-';
const AUTH_SESSION_HINT_COOKIE_VALUE = '1';
const AUTH_SESSION_HINT_DEFAULT_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

type HeadersLike = Headers | Request;
type AuthSessionHintCookie = {
  name: string;
  value: string;
  options: {
    path: string;
    sameSite: 'lax';
    secure: boolean;
    maxAge: number;
    expires: Date;
  };
};

function getCookieHeader(input: HeadersLike): string | null {
  const headers = input instanceof Headers ? input : input.headers;
  return headers.get('cookie');
}

function parseCookieValue(
  cookieHeader: string,
  cookieNames: readonly string[]
): string | null {
  for (const part of cookieHeader.split(/;\s*/)) {
    if (!part) continue;
    const separatorIndex = part.indexOf('=');
    if (separatorIndex <= 0) continue;

    const name = part.slice(0, separatorIndex).trim();
    if (!cookieNames.includes(name)) {
      continue;
    }

    return part.slice(separatorIndex + 1).trim();
  }

  return null;
}

function resolveHintCookieMaxAgeSeconds(expiresAtMs?: number | null) {
  if (typeof expiresAtMs === 'number' && Number.isFinite(expiresAtMs)) {
    return Math.max(0, Math.floor((expiresAtMs - Date.now()) / 1000));
  }

  return AUTH_SESSION_HINT_DEFAULT_MAX_AGE_SECONDS;
}

export function getAuthSessionHintCookieName(secure: boolean) {
  return secure
    ? `${SECURE_COOKIE_PREFIX}${AUTH_SESSION_HINT_COOKIE_BASE_NAME}`
    : AUTH_SESSION_HINT_COOKIE_BASE_NAME;
}

export function getAuthSessionHintCookieCandidates() {
  return [
    getAuthSessionHintCookieName(true),
    getAuthSessionHintCookieName(false),
  ] as const;
}

export function hasAuthSessionHintCookie(input: HeadersLike): boolean {
  const cookieHeader = getCookieHeader(input);
  if (!cookieHeader) {
    return false;
  }

  return (
    parseCookieValue(cookieHeader, getAuthSessionHintCookieCandidates()) ===
    AUTH_SESSION_HINT_COOKIE_VALUE
  );
}

export function appendAuthSessionHintCookieHeader(params: {
  headers: Headers;
  secure: boolean;
  expiresAtMs?: number | null;
}) {
  const cookie = buildAuthSessionHintCookie(params);

  params.headers.append(
    'Set-Cookie',
    serializeCookie(cookie.name, cookie.value, cookie.options)
  );
}

export function buildAuthSessionHintCookie(params: {
  secure: boolean;
  expiresAtMs?: number | null;
}): AuthSessionHintCookie {
  const maxAgeSeconds = resolveHintCookieMaxAgeSeconds(params.expiresAtMs);

  return {
    name: getAuthSessionHintCookieName(params.secure),
    value: AUTH_SESSION_HINT_COOKIE_VALUE,
    options: {
      path: '/',
      sameSite: 'lax',
      secure: params.secure,
      maxAge: maxAgeSeconds,
      expires: new Date(Date.now() + maxAgeSeconds * 1000),
    },
  };
}

export function appendClearedAuthSessionHintCookieHeader(params: {
  headers: Headers;
  secure: boolean;
}) {
  for (const secure of [true, false]) {
    params.headers.append(
      'Set-Cookie',
      serializeCookie(getAuthSessionHintCookieName(secure), '', {
        path: '/',
        sameSite: 'lax',
        secure,
        maxAge: 0,
        expires: new Date(0),
      })
    );
  }
}
